import React, { useState, useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import backdateddays_icon from "../../assets/svg/backdateddays_icon.svg";
import { ToastContainer, toast } from "react-toastify";
import axiosInstance from "../../axiosInstance";
import { useNavigate } from "react-router-dom";
import SapCsvSafeInput from '../../CommonRemarksRestriction/SapCsvSafeInput';

const GenerateSeparatePopup = ({
    isOpen,
    onClose,
    separateChargesData = null,
    loading = false,
    projectId,
    backdatedDays = 0
}) => {
    const [lineItems, setLineItems] = useState([]);
    const [saveMode, setSaveMode] = useState(true);
    const [isLoading, setIsLoading] = useState(false);
    const [globalBackdatedDate, setGlobalBackdatedDate] = useState("");
    const [globalBillingNature, setGlobalBillingNature] = useState("");
    const [globalSupplyPeriod, setGlobalSupplyPeriod] = useState(null);
    const [errors, setErrors] = useState({});
    const globalDatePickerRef = useRef();
    const navigate = useNavigate();

    useEffect(() => {

        if (isOpen && separateChargesData) {
            const flattenedItems = [];

            const combineSeparateArray = separateChargesData.combine_separate_multiple || separateChargesData.combine_separate;

            const lineItemDetailsMap = {};

            if (separateChargesData.line_items && Array.isArray(separateChargesData.line_items)) {
                separateChargesData.line_items.forEach(job => {
                    if (job.details && Array.isArray(job.details)) {
                        job.details.forEach(detail => {
                            lineItemDetailsMap[detail.job_sales_line_id] = {
                                ...detail,
                                job_no: job.job_no,
                                project_id: job.project_id,
                                backdated_days: job.backdated_days,
                                ff_plant_id: job.ff_plant_id,
                                division_type: detail.job_type_division?.toLowerCase(),
                                supply_period: job.supply_period
                            };
                        });
                    }
                });
            }

            if (combineSeparateArray && Array.isArray(combineSeparateArray)) {
                console.log("✅ Processing combine_separate_multiple structure, found", combineSeparateArray.length, "items");

                combineSeparateArray.forEach((combinedItem, index) => {
                    console.log(`📝 Processing combine_separate item ${index}:`, combinedItem);

                    const jobLineIds = combinedItem.job_line_ids
                        ? combinedItem.job_line_ids.split(',').map(id => id.trim())
                        : [];

                    const lineItemDetails = [];
                    jobLineIds.forEach(id => {
                        if (lineItemDetailsMap[id]) {
                            lineItemDetails.push(lineItemDetailsMap[id]);
                        }
                    });

                    console.log(`📊 Found ${lineItemDetails.length} line item details for combined item ${index}:`, lineItemDetails);

                    const isCombinedCharge = jobLineIds.length > 1;
                    const firstDetail = lineItemDetails[0] || {};

                    let chargeCode = firstDetail.charge_code;
                    if (!chargeCode && lineItemDetails.length > 0) {
                        for (const detail of lineItemDetails) {
                            if (detail.charge_code) {
                                chargeCode = detail.charge_code;
                                break;
                            }
                        }
                    }
                    if (!chargeCode) {
                        chargeCode = combinedItem.material_code;
                    }

                    flattenedItems.push({
                        id: `combined_${index}_${Date.now()}`,
                        isCombined: isCombinedCharge,
                        source: "combined",
                        job_line_ids: combinedItem.job_line_ids,
                        material_code: combinedItem.material_code,
                        charge_code: chargeCode,
                        description: combinedItem.description_of_charges,
                        quantity: combinedItem.unit_qty,
                        rate: combinedItem.unit_rate_price,
                        net_total: combinedItem.net_total,
                        remarks: combinedItem.remarks || "",
                        supply_period: firstDetail.supply_period || null,
                        plant_id: combinedItem.plant_id || combinedItem.plant_code || "",
                        plant_code: combinedItem.plant_id || "",

                        job_no: firstDetail.job_no || "",
                        job_id: firstDetail.job_id,
                        job_line_id: firstDetail.job_sales_line_id,
                        project_id: firstDetail.project_id || combinedItem.project_id,
                        charge_id: firstDetail.charge_id,
                        job_type_id: firstDetail.job_type_id,

                        backdated_date: "",
                        sales_header_remarks: "",
                        billing_nature: "",
                        supply_period: firstDetail.supply_period || null,
                        backdated_days: firstDetail.backdated_days || backdatedDays,
                        rawItem: combinedItem,
                        lineItemDetails: lineItemDetails,
                        ff_plant_id: firstDetail.ff_plant_id,
                        division_type: firstDetail.job_type_division,
                    });
                });
            }

            if (separateChargesData.line_items && Array.isArray(separateChargesData.line_items)) {
                console.log("✅ Processing line_items structure");

                const alreadyProcessedIds = new Set();
                if (combineSeparateArray && Array.isArray(combineSeparateArray)) {
                    combineSeparateArray.forEach(item => {
                        if (item.job_line_ids) {
                            const ids = item.job_line_ids.split(',').map(id => id.trim());
                            ids.forEach(id => alreadyProcessedIds.add(id));
                        }
                    });
                }

                console.log("🚫 Already processed IDs:", Array.from(alreadyProcessedIds));

                separateChargesData.line_items.forEach(job => {
                    if (job.details && Array.isArray(job.details)) {
                        job.details.forEach(detail => {
                            const isAlreadyIncluded = alreadyProcessedIds.has(detail.job_sales_line_id.toString());

                            if (!isAlreadyIncluded) {
                                console.log(`➕ Adding individual item: ${detail.job_sales_line_id} - ${detail.charge_code}`);
                                flattenedItems.push({
                                    id: `${job.job_no}_${detail.charge_id}_${detail.job_sales_line_id}`,
                                    isCombined: false,
                                    source: "individual",
                                    ff_plant_id: job.ff_plant_id,
                                    division_type: detail.job_type_division?.toLowerCase(),
                                    job_no: job.job_no,
                                    job_id: detail.job_id,
                                    job_line_id: detail.job_sales_line_id,
                                    charge_code: detail.charge_code,
                                    material_code: detail.sap_code,
                                    sap_description: detail.sap_description,
                                    description: detail.description,
                                    quantity: detail.sales_unit_of_quantity,
                                    rate: detail.sales_unit_of_rate,
                                    net_total: detail.sales_net_total,
                                    remarks: detail.sales_remarks || "",
                                    backdated_date: "",
                                    project_id: job.project_id,
                                    plant_id: detail.plant_code || "",
                                    plant_code: detail.plant_code || "",
                                    backdated_days: job.backdated_days || backdatedDays,
                                    sales_header_remarks: "",
                                    billing_nature: "",
                                    supply_period: detail.supply_period
                                        ? new Date(detail.supply_period)
                                        : null,
                                    sap_id: detail.sap_id,
                                    sap_code: detail.sap_code,
                                    sap_description: detail.sap_description
                                });
                            } else {
                                console.log(`➖ Skipping already included item: ${detail.job_sales_line_id}`);
                            }
                        });
                    }
                });
            }

            console.log("📊 Final flattened items:", flattenedItems);
            console.log("📈 Total items:", flattenedItems.length);
            console.log("🔍 Combined items:", flattenedItems.filter(item => item.isCombined));
            console.log("🔍 Individual items:", flattenedItems.filter(item => !item.isCombined));

            setLineItems(flattenedItems);
            setGlobalBackdatedDate("");
            setGlobalBillingNature("");
            setGlobalSupplyPeriod(null);
            setSaveMode(true);
            setErrors({});
        } else if (isOpen) {
            console.log("⚠️ isOpen is true but separateChargesData is", separateChargesData);
            setLineItems([]);
        }
    }, [isOpen, separateChargesData, backdatedDays]);

    useEffect(() => {
        if (globalBackdatedDate && lineItems.length > 0) {
            setLineItems(prev =>
                prev.map(item => ({
                    ...item,
                    backdated_date: globalBackdatedDate
                }))
            );
        }
    }, [globalBackdatedDate, lineItems.length]);

    useEffect(() => {
        setLineItems(prev =>
            prev.map(item => ({
                ...item,
                billing_nature: globalBillingNature || ""
            }))
        );
    }, [globalBillingNature]);


    useEffect(() => {
        if (globalSupplyPeriod) {
            setLineItems(prev =>
                prev.map(item => ({
                    ...item,
                    supply_period: globalSupplyPeriod
                }))
            );
        }
    }, [globalSupplyPeriod]);


    const getMinBackdatedDate = (currentDate, days) => {
        const minDate = new Date(currentDate);
        minDate.setDate(minDate.getDate() - (days || 0));
        return minDate;
    };

    const handleLineItemChange = (id, field, value) => {
        setLineItems(prev => prev.map(item =>
            item.id === id ? { ...item, [field]: value } : item
        ));
        setErrors(prev => ({
            ...prev,
            [id]: {
                ...prev[id],
                [field]: undefined
            }
        }));
    };

    const formatBackdatedDate = (d) => {
        const dt = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
        return dt.toISOString().split('T')[0];
    };

    const formatDate = (d, isPicker = false) => {
        if (!d) return "";

        const date = isPicker
            ? new Date(d.getTime() - d.getTimezoneOffset() * 60000)
            : new Date(d);

        const day = String(date.getDate()).padStart(2, "0");
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const year = date.getFullYear();

        return `${day}.${month}.${year}`;
    };

    // const validateForSubmit = () => {
    //     let errObj = {};

    //     lineItems.forEach((item) => {
    //         let itemErrors = {};

    //         if (!item.sales_header_remarks?.trim()) {
    //             itemErrors.sales_header_remarks = "Remarks to Customer is required";
    //         }
    //         if (!item.billing_nature?.trim()) {
    //             itemErrors.billing_nature = "Billing Nature is required";
    //         }
    //         if (!item.supply_period) {
    //             itemErrors.supply_period = "Supply Period is required";
    //         }

    //         if (Object.keys(itemErrors).length > 0) {
    //             errObj[item.id] = itemErrors;
    //         }
    //     });

    //     return errObj;
    // };

    const getBaseProjectCode = (projectCode = "") =>
        projectCode.split("-")[0];

    const buildPayload = (actionType) => {
        const formatSupplyPeriod = (date) => {
            if (!date) return null;
            const d = new Date(date);
            return `${d.toLocaleString("en-US", { month: "short" })}-${d.getFullYear()}`;
        };

        const jobsMap = {};
        const salesHeaderRemarksObj = {};
        const billingNatureObj = {};
        const supplyPeriodObj = {};
        const backdatedDateObj = {};
        const combineSeparateMultiplePayload = [];

        lineItems.forEach((item) => {
            const plantId = item.rawItem?.plant_id ||
                item.rawItem?.plant_code ||
                item.plant_id ||
                item.plant_code ||
                "";
            if (item.isCombined) {
                combineSeparateMultiplePayload.push({
                    job_line_ids: item.job_line_ids,
                    project_id: getBaseProjectCode(item.rawItem?.project_id || ""),
                    plant_id: plantId,
                    bill_date: item.backdated_date
                        ? formatDate(new Date(item.backdated_date))
                        : "",
                    supply_period: formatSupplyPeriod(item.supply_period) || "",
                    billing_nature: item.billing_nature || "",
                    po_wo_no: item.rawItem?.po_wo_no || "",
                    po_wo_date: item.rawItem?.po_wo_date || "",
                    material_code: item.material_code,
                    description_of_charges: item.description,
                    unit_qty: item.quantity,
                    unit_rate_price: item.rate,
                    net_total: item.net_total,
                    remarks: item.sales_header_remarks || item.remarks || ""
                });

                if (item.job_line_ids) {
                    const ids = item.job_line_ids.split(",").map(id => id.trim());
                    ids.forEach(id => {
                        backdatedDateObj[id] =
                            item.backdated_date && item.backdated_date !== ""
                                ? formatBackdatedDate(new Date(item.backdated_date))
                                : null;
                    });
                } else {
                    backdatedDateObj[item.job_line_id] =
                        item.backdated_date
                            ? item.backdated_date
                            : null;
                }

            } else {
                combineSeparateMultiplePayload.push({
                    job_line_ids: item.job_line_id.toString(),
                    project_id: getBaseProjectCode(item.rawItem?.project_id || ""),
                    plant_id: plantId,
                    bill_date: item.backdated_date
                        ? formatDate(new Date(item.backdated_date))
                        : "",
                    supply_period: formatSupplyPeriod(item.supply_period) || "",
                    billing_nature: item.billing_nature || "",
                    po_wo_no: item.po_wo_number || "",
                    po_wo_date: item.po_wo_date || "",
                    material_code: item.material_code,
                    description_of_charges: item.description,
                    unit_qty: item.quantity,
                    unit_rate_price: item.rate,
                    net_total: item.net_total,
                    remarks: item.sales_header_remarks || item.remarks || ""
                });

                salesHeaderRemarksObj[item.job_line_id] = item.sales_header_remarks || "";
                billingNatureObj[item.job_line_id] = item.billing_nature || "";
                supplyPeriodObj[item.job_line_id] = item.supply_period
                    ? formatSupplyPeriod(item.supply_period)
                    : "";
                backdatedDateObj[item.job_line_id] =
                    item.backdated_date && item.backdated_date !== ""
                        ? formatBackdatedDate(new Date(item.backdated_date))
                        : null;
            }

            if (item.job_id) {
                if (!jobsMap[item.job_id]) {
                    jobsMap[item.job_id] = {
                        id: item.job_id,
                        jobnumber: item.job_no || `JOB_${item.job_id}`,
                        project_id: projectId || "",
                        job_type_id: item.job_type_id || "",
                        job_created_date: new Date().toISOString().split("T")[0],
                        ff_plant_id: item.ff_plant_id,
                        job_line: [],
                    };
                }

                if (item.isCombined && item.lineItemDetails) {
                    item.lineItemDetails.forEach(detail => {
                        jobsMap[item.job_id].job_line.push({
                            project_id: projectId || "",
                            job_type_id: detail.job_type_id || item.job_type_id || "",
                            job_line_id: detail.job_sales_line_id,
                            charge_id: detail.charge_id || item.charge_id || "",
                            description_of_charges: detail.description || item.description,
                            po_wo_number: detail.sales_po_wo_number || item.po_wo_number || null,
                            po_wo_date: detail.sales_po_wo_date || item.po_wo_date || null,
                            unit_of_quantity: parseFloat(detail.sales_unit_of_quantity) || parseFloat(item.quantity) || 0,
                            accrual: parseFloat(detail.sales_accrual) || parseFloat(item.accrual) || 0,
                            contract_value: parseFloat(detail.sales_contract_value) || parseFloat(item.contract_value) || 0,
                            remarks: detail.sales_remarks || item.remarks || "",
                            unit_of_rate: parseFloat(detail.sales_unit_of_rate) || parseFloat(item.rate) || 0,
                            net_total: parseFloat(detail.sales_net_total) || parseFloat(item.net_total) || 0,
                            attachment_keys: detail.attachment_keys || item.attachment_keys || {}
                        });
                    });
                } else {
                    jobsMap[item.job_id].job_line.push({
                        project_id: projectId || "",
                        job_type_id: item.job_type_id || "",
                        job_line_id: item.job_line_id,
                        charge_id: item.charge_id || "",
                        description_of_charges: item.description,
                        po_wo_number: item.po_wo_number || null,
                        po_wo_date: item.po_wo_date || null,
                        unit_of_quantity: parseFloat(item.quantity) || 0,
                        accrual: parseFloat(item.accrual) || 0,
                        contract_value: parseFloat(item.contract_value) || 0,
                        remarks: item.remarks || "",
                        unit_of_rate: parseFloat(item.rate) || 0,
                        net_total: parseFloat(item.net_total) || 0,
                        attachment_keys: item.attachment_keys || {}
                    });
                }
            }
        });

        const jobs = Object.values(jobsMap);

        const payload = {
            jobs: jobs.length > 0 ? jobs : [],
            actions: [actionType],
            invoice_target: "sales",
            invoice_mode: "combine_separate_multiple",
            generate_invoice: "1",
            sales_header_remarks: salesHeaderRemarksObj || "",
            supply_period: supplyPeriodObj || "",
            billing_nature: billingNatureObj || "",
            backdated_date: backdatedDateObj,
            division_type: lineItems?.[0]?.job_type_division?.toLowerCase() || "",
            combine_separate_multiple: combineSeparateMultiplePayload
        };

        console.log("📤 GenerateSeparatePopup final payload:", payload);
        return payload;
    };

    const handleSubmit = async (actionType) => {
        if (isLoading) return;
        setIsLoading(true);

        try {
            if (actionType === "submit") {
                // const validationErrors = validateForSubmit();
                // if (Object.keys(validationErrors).length > 0) {
                //     setErrors(validationErrors);
                //     setIsLoading(false);
                //     return;
                // }
            }

            const payload = buildPayload(actionType);

            // Format the data as expected by backend
            const formData = new FormData();
            formData.append("data", JSON.stringify(payload));

            console.log("🚀 Final API Payload:", payload);

            const response = await axiosInstance.post("/invoice/save", formData, {
                headers: { "Content-Type": "multipart/form-data" },
            });

            if (response.status === 200 && response.data.success === true) {
                toast.success(
                    `Sales Invoice ${actionType === "save" ? "saved" : "submitted"} successfully!`
                );
                sessionStorage.removeItem("mergedChargesData");
                sessionStorage.removeItem("separateChargesData");
                onClose();
                navigate("/salesInvoice");
            } else {
                console.error("API Error Response:", response.data);
                toast.error(response.data.message || "Failed to process invoice");
            }
        } catch (error) {
            console.error("Error submitting separate invoice:", error);
            if (error.response) {
                console.error("Error response data:", error.response.data);
                console.error("Error response status:", error.response.status);
            }
            toast.error(
                "Error while submitting: " + (error.response?.data?.message || error.message)
            );
        } finally {
            setIsLoading(false);
        }
    };

    // const handleSave = () => {
    //     if (lineItems.length === 0) {
    //         toast.warning("No line items to save");
    //         return;
    //     }
    //     handleSubmit("save");
    // };

    const handleFinalSubmit = () => {
        if (lineItems.length === 0) {
            toast.warning("No line items to submit");
            return;
        }
        handleSubmit("submit");
    };

    if (!isOpen) return null;

    return (
        <>
            <div className="separate-invoice-popup-overlay">
                <div className="separate-invoice-popup-container">
                    <div className="separate-invoice-popup-header">
                        <h2 style={{ fontSize: '16px' }}>Separate Invoice Creation</h2>

                        <div className="separate-invoice-header-right">
                            {/* <p>Backdated Date for all :</p>

                            <div className="global-backdated-wrapper">
                                <DatePicker
                                    ref={globalDatePickerRef}
                                    selected={globalBackdatedDate ? new Date(globalBackdatedDate) : null}
                                    onChange={(date) => {
                                        const formatted = date ? formatDate(date) : "";
                                        setGlobalBackdatedDate(formatted);
                                    }}
                                    isClearable={!!globalBackdatedDate}
                                    dateFormat="yyyy-MM-dd"
                                    className="custom-datepicker-input global-backdated-input"
                                    placeholderText="Select a backdated date"
                                    minDate={getMinBackdatedDate(new Date(), backdatedDays)}
                                    maxDate={new Date()}
                                />

                                {!globalBackdatedDate && (
                                    <img
                                        src={backdateddays_icon}
                                        className="global-backdated-icon"
                                        alt="backdated"
                                    />
                                )}
                            </div>

                            <div className="separate-backdated-date-divider"></div> */}

                            {/* <button
                                className="separate-invoice-btn-save"
                                onClick={handleSave}
                                disabled={isLoading || lineItems.length === 0}
                            >
                                {isLoading ? 'Saving...' : 'Save'}
                            </button> */}
                            <button
                                className="separate-invoice-btn-submit"
                                onClick={handleFinalSubmit}
                                disabled={isLoading || lineItems.length === 0}
                            >
                                {isLoading ? 'Submitting...' : 'Submit'}
                            </button>
                            <div
                                className="merged-close-btn"
                                onClick={onClose}
                                style={{ cursor: 'pointer' }}
                            >
                                <X size={16} />
                            </div>
                        </div>
                    </div>

                    <div className="separate-invoice-scroll-area">
                        <div className="global-header">
                            <h4 className="global-title">Global Invoice Fields</h4>
                            <div className="global-subtitle">
                                <span className="info-icon">!</span>
                                <span>Values entered here will be applied to all invoice line items</span>
                            </div>
                        </div>
                        <div className="global-wrapper-all">
                            <div className="global-field" style={{ position: "relative" }}>
                                <label className="global-label">Billing Nature for all</label>
                                <SapCsvSafeInput
                                    className="global-input"
                                    placeholder="Enter Billing Nature"
                                    value={globalBillingNature}
                                    onChange={setGlobalBillingNature}
                                    maxLength={18}
                                    style={{ margin: "0px", paddingRight: "30px" }}
                                />
                                {globalBillingNature && (
                                    <span
                                        onClick={() => setGlobalBillingNature("")}
                                        title="Clear"
                                        style={{
                                            position: "absolute",
                                            right: "8px",
                                            top: "70%",
                                            transform: "translateY(-50%)",
                                            cursor: "pointer",
                                            width: "20px",
                                            height: "20px",
                                            borderRadius: "50%",
                                            backgroundColor: "#216ba5",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            boxShadow: "0 0 0 1px #ddd",
                                        }}
                                    >
                                        <X size={10} color="#fff" />
                                    </span>
                                )}

                            </div>

                            <div className="global-field">
                                <label className="global-label">Supply Period for all</label>
                                <DatePicker
                                    selected={globalSupplyPeriod}
                                    onChange={(date) => {
                                        setGlobalSupplyPeriod(date);

                                        setLineItems(prev =>
                                            prev.map(item => ({
                                                ...item,
                                                supply_period: date
                                            }))
                                        );
                                    }}
                                    dateFormat="MMM-yyyy"
                                    showMonthYearPicker
                                    className="global-supplyperiod-input"
                                    placeholderText="Select month & year"
                                    isClearable
                                    disabled
                                />

                            </div>

                            <div className="global-field">
                                <label className="global-label">Backdated Date for all :</label>
                                <div className="global-backdated-wrapper">
                                    <DatePicker
                                        ref={globalDatePickerRef}
                                        selected={globalBackdatedDate ? new Date(globalBackdatedDate) : null}
                                        onChange={(date) => {
                                            const formatted = date ? formatBackdatedDate(date) : null;
                                            setGlobalBackdatedDate(formatted);

                                            setLineItems(prev =>
                                                prev.map(item => ({
                                                    ...item,
                                                    backdated_date: formatted
                                                }))
                                            );
                                        }}
                                        isClearable={!!globalBackdatedDate}
                                        dateFormat="yyyy-MM-dd"
                                        className="global-backdated-input"
                                        placeholderText="Select a backdated date"
                                        minDate={getMinBackdatedDate(new Date(), backdatedDays)}
                                        maxDate={new Date()}
                                    />

                                    {!globalBackdatedDate && (
                                        <img
                                            src={backdateddays_icon}
                                            className="global-backdated-icon"
                                            alt="backdated"
                                        />
                                    )}
                                </div>
                            </div>

                        </div>
                        {loading ? (
                            <div className="loading-container">
                                <p>Loading separate charges...</p>
                            </div>
                        ) : lineItems.length === 0 ? (
                            <div className="no-data-container">
                                <p>No separate charges to display</p>
                            </div>
                        ) : (
                            lineItems.map((item, index) => (
                                <div key={item.id} className='separate-invoice-main-card'>
                                    <div className="separate-invoice-card-section">
                                        <div className="separate-invoice-card">
                                            <div className="separate-left-block">
                                                <p className="separate-title">{item.description}</p>
                                                <p className="separate-code">Charge code : <span>{item.charge_code}</span></p>
                                            </div>

                                            <div className="separate-divider"></div>

                                            <div className="separate-info-box">
                                                <p className="label">Quantity</p>
                                                <p className="value">{parseFloat(item.quantity).toFixed(2)}</p>
                                            </div>

                                            <div className="separate-info-box">
                                                <p className="label">Rate</p>
                                                <p className="value">₹ {parseFloat(item.rate).toLocaleString('en-IN')}</p>
                                            </div>

                                            <div className="separate-info-box">
                                                <p className="label">Net Total</p>
                                                <p className="value">₹ {parseFloat(item.net_total).toLocaleString('en-IN')}</p>
                                            </div>

                                            <div className="separate-info-box full-width">
                                                <p className="label">Remarks</p>
                                                <p className="value" title={item.remarks}>
                                                    {item.remarks && item.remarks.length > 28
                                                        ? item.remarks.slice(0, 28) + "..."
                                                        : item.remarks || "--"}
                                                </p>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="separate-invoice-card-section second">
                                        <div className="separate-invoice-grid-2">
                                            <div className="separate-input-wrapper">
                                                <label className="separate-input-label">
                                                    Remarks to Customer
                                                </label>

                                                <div className="input-with-counter">
                                                    <SapCsvSafeInput
                                                        className="separate-invoice-input-box"
                                                        placeholder="Remarks to Customer"
                                                        value={item.sales_header_remarks || ""}
                                                        onChange={(val) =>
                                                            handleLineItemChange(item.id, "sales_header_remarks", val)
                                                        }
                                                        maxLength={255}
                                                        required={!saveMode}
                                                    />

                                                    <span className="separate-input-counter">
                                                        {(item.sales_header_remarks?.length || 0)}/255
                                                    </span>
                                                </div>

                                                {errors[item.id]?.sales_header_remarks && (
                                                    <p className="separate-input-error">{errors[item.id].sales_header_remarks}</p>
                                                )}
                                            </div>

                                            <div className="separate-input-wrapper">
                                                <label style={{ color: '#212129', fontWeight: 600 }}>
                                                    Billing Nature
                                                </label>

                                                <div className="input-with-counter">
                                                    <SapCsvSafeInput
                                                        className="separate-invoice-input-box"
                                                        placeholder="Enter billing nature"
                                                        value={item.billing_nature || ""}
                                                        onChange={(val) =>
                                                            handleLineItemChange(item.id, "billing_nature", val)
                                                        }
                                                        maxLength={18}
                                                        required={!saveMode}
                                                        style={{ paddingRight: "40px" }}
                                                    />

                                                    <span className="separate-input-counter"
                                                        style={{
                                                            position: "absolute",
                                                            right: "10px",
                                                            bottom: "12px",
                                                            pointerEvents: "none",
                                                            fontSize: "11px",
                                                            color: "#777",
                                                        }}
                                                    >
                                                        {(item.billing_nature?.length || 0)}/18
                                                    </span>
                                                </div>
                                                {errors[item.id]?.billing_nature && (
                                                    <p className="separate-input-error">
                                                        {errors[item.id].billing_nature}
                                                    </p>
                                                )}
                                            </div>

                                            <div>
                                                <label style={{ color: '#212129', fontWeight: 600 }}>
                                                    Supply Period
                                                </label>
                                                <div className="supply-period-picker">
                                                    <DatePicker
                                                        selected={item.supply_period}
                                                        onChange={(date) =>
                                                            handleLineItemChange(item.id, "supply_period", date)
                                                        }
                                                        dateFormat="MMM-yyyy"
                                                        showMonthYearPicker
                                                        className="separate-invoice-input-box"
                                                        placeholderText="Select month & year"
                                                        required={!saveMode}
                                                        disabled
                                                    />
                                                </div>
                                                {errors[item.id]?.supply_period && (
                                                    <p style={{ color: "red", fontSize: "12px", marginTop: "3px" }}>
                                                        {errors[item.id].supply_period}
                                                    </p>
                                                )}
                                            </div>

                                            <div>
                                                <label style={{ color: '#212129', fontWeight: 600 }}>
                                                    Backdated Date
                                                </label>
                                                <div className="supply-period-picker">
                                                    <DatePicker
                                                        selected={item.backdated_date ? new Date(item.backdated_date) : null}
                                                        onChange={(date) => {
                                                            handleLineItemChange(
                                                                item.id,
                                                                "backdated_date",
                                                                date ? formatBackdatedDate(date) : null
                                                            );
                                                        }}
                                                        dateFormat="yyyy-MM-dd"
                                                        className="separate-invoice-input-box backdateddays-input"
                                                        placeholderText="Select a backdated date"
                                                        minDate={getMinBackdatedDate(new Date(), item.backdated_days || 0)}
                                                        maxDate={new Date()}
                                                    />

                                                    <img src={backdateddays_icon} className="backdateddays-icon" alt="calendar" />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </div>
            </div>
            <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} />
        </>
    );
};

export default GenerateSeparatePopup;